import apiBaseState from "~/constants/apiBaseState";
import Vueset from "~/utils/Vueset";

let globalSetupValuesRequest = null;

export const state = () => ({
    account: apiBaseState(),
    project: apiBaseState(),
    accounts: apiBaseState(),
    projects: apiBaseState(),
    projectsForAccountState: apiBaseState(),
    restoreProjectState: apiBaseState(),
    selectedProject: null,
    projectCreateState: apiBaseState(),
    projectUpdateState: apiBaseState(),
    projectDeleteState: apiBaseState(),
    setDefaultAccountState: apiBaseState(),
    dashboard: apiBaseState(),
    globalSetupValuesState: apiBaseState(),
    accountCreateState: apiBaseState(),
    accountUpdateState: apiBaseState(),
    accountDeleteState: apiBaseState(),
    accountUploadState: apiBaseState(),
    sidebarOpen:
        typeof window !== 'undefined' && localStorage.getItem('sidebarOpen') !== null
            ? JSON.parse(localStorage.getItem('sidebarOpen'))
            : true,
    sidebarMobileToggle: false,
    isMobile: false,
    loggingOut: false,
    themes: null,
    tenants: null,
    currentTaskStatus: null,
    modalUnsaved: false,
    savedChangesTracker: false,
    // Instead of a single value, preferredLanguages is now an array.
    // It is initialized from localStorage if available.
    preferredLanguages:
        process.client && localStorage.getItem("preferredLanguages")
            ? JSON.parse(localStorage.getItem("preferredLanguages"))
            : [],
    controls: {
        groupingSensitivity: 0,
        imageOpacity: 1,
        heatmapOpacity: 1,
        heatmapRadius: 9,
        heatmapSpacer: 15,
    },
});

export const getters = {
    hasSelectedProject(state) {
        return state.selectedProject !== null;
    },
    projects(state) {
        if (!state.projectsForAccountState.data) {
            return [];
        }
        return state.projectsForAccountState;
    },
    isAnonymised(state, getters, rootState) {
        return !!state.project.data?.anonymised && !rootState.auth.user.isSuperAdmin;
    },
    controls: (state) => state.controls,
    controlValue: (state) => (controlName) => {
        return parseFloat(state.controls[controlName]);
    },
    // Getter to retrieve the preferred language for a given taskSlug.
    getPreferredLanguage: (state) => (taskSlug) => {
        const entry = state.preferredLanguages.find(
            (item) => item.taskSlug === taskSlug
        );
        return entry ? entry.language.replace('en_uk','en') : "";
    },
};

export const mutations = {
    // Mutation to set a preferred language for a given taskSlug.
    // If `language` is falsy (e.g. an empty string) the entry is removed (reset).
    SET_PREFERRED_LANGUAGE(state, { taskSlug, language }) {
        const index = state.preferredLanguages.findIndex(
            (item) => item.taskSlug === taskSlug
        );
        if (index !== -1) {
            if (language) {
                state.preferredLanguages.splice(index, 1, { taskSlug, language });
            } else {
                state.preferredLanguages.splice(index, 1);
            }
        } else {
            if (language) {
                state.preferredLanguages.push({ taskSlug, language });
            }
        }
        if (process.client) {
            localStorage.setItem(
                "preferredLanguages",
                JSON.stringify(state.preferredLanguages)
            );
        }
    },
    SET_CONTROL_VALUE(state, { controlName, value }) {
        if (state.controls.hasOwnProperty(controlName)) {
            state.controls[controlName] = value;
        }
    },
    SET_MODAL_UNSAVED(state, payload) {
        state.modalUnsaved = payload;
    },
    SET_SAVED_CHANGES_TRACKER(state, payload) {
        state.savedChangesTracker = payload;
    },
    SET_THEMES(state, payload) {
        state.themes = payload;
    },
    SET_TENANTS(state, payload) {
        state.tenants = payload;
    },
    CLEAR_DEFAULT_ACCOUNTS(state) {
        state.accounts.data.forEach((account) => {
            account.default = false;
        });
    },
    SET_DELETE_ACCOUNT_STATE(state, payload) {
        state.accountDeleteState = payload;
    },
    SET_CREATE_ACCOUNT_STATE(state, payload) {
        state.accountCreateState = payload;
    },
    SET_UPDATE_ACCOUNT_STATE(state, payload) {
        state.accountUpdateState = payload;
    },
    SET_DEFAULT_PROJECT(state, payload) {
        state.setDefaultAccountState = payload;
    },
    SET_ACCOUNTS(state, payload) {
        state.accounts = payload;
    },
    SET_PROJECTS(state, payload) {
        state.projects = payload;
    },
    SET_RESTORE_PROJECT(state, payload) {
        state.restoreProjectState = payload;
    },
    RESET_RESTORE_PROJECT(state) {
        state.restoreProjectState = apiBaseState();
    },
    SET_PROJECTS_FOR_ACCOUNT(state, payload) {
        state.projectsForAccountState = payload;
    },
    SET_PROJECT_CREATION(state, payload) {
        state.projectCreateState = payload;
    },
    SET_SELECTED_PROJECT(state, payload) {
        state.selectedProject = payload;
    },
    SET_SIDEBAR_OPEN(state, value) {
        state.sidebarOpen = value;
        if (typeof window !== 'undefined') {
            localStorage.setItem('sidebarOpen', JSON.stringify(value));
        }
    },
    ADD_NEW_PROJECT(state, project) {
        state.projectsForAccountState.data.data.push(project);
        state.projectCreateState = apiBaseState();
    },
    SET_PROJECT_STATE(state, payload) {
        state.project = payload;
    },
    SET_PROJECT_UPDATE_STATE(state, payload) {
        state.projectUpdateState = payload;
    },
    SET_PROJECT_DELETE_STATE(state, payload) {
        state.projectDeleteState = payload;
    },
    UPDATE_SINGLE_PROJECT(state, { project }) {
        if (state.selectedProject) {
            state.selectedProject = project;
        }
        if (state.projectsForAccountState.data) {
            const projectIndex = state.projectsForAccountState.data.data.findIndex(
                (existingProject) => existingProject.slug === project.slug
            );
            const projects = state.projectsForAccountState.data.data;
            Vueset(projects, projectIndex, project);
        }
        if (state.project) {
            state.project.data = project;
        }
    },
    UPDATE_ACCOUNT(state, account) {
        const accountIndex = state.accounts.data.findIndex(
            (existingAccount) => existingAccount.slug === account.slug
        );
        const accounts = state.accounts.data;
        Vueset(accounts, accountIndex, account);
    },
    ADD_NEW_ACCOUNT(state, account) {
        state.accounts.data.push(account.data);
    },
    DELETE_SINGLE_ACCOUNT(state, { accountSlug }) {
        const accountIndex = state.accounts.data.findIndex(
            (existingAccount) => existingAccount.slug === accountSlug
        );
        state.accounts.data.splice(accountIndex, 1);
    },
    DELETE_SINGLE_PROJECT(state, { projectSlug }) {
        const projectIndex = state.projectsForAccountState.data.data.findIndex(
            (project) => project.slug === projectSlug
        );
        state.projectsForAccountState.data.data.splice(projectIndex, 1);
    },
    SET_ACCOUNT(state, payload) {
        state.account = payload;
    },
    SET_DASHBOARD(state, payload) {
        state.dashboard = payload;
    },
    SET_GLOBAL_SETUP_VALUES(state, payload) {
        state.globalSetupValuesState = payload;
    },
    REMOVE_TASK_FROM_DASHBOARD_BY_SLUG(state, { taskSlug }) {
        const index = state.dashboard.data.data.findIndex(
            (item) => item.slug === taskSlug
        );
        if (index > -1) {
            state.dashboard.data.data.splice(index, 1);
        }
    },
    UPDATE_TASK_FROM_DASHBOARD(state, task) {
        const index = state.dashboard.data.data.findIndex(
            (item) => item.slug === task.slug
        );
        if (index > -1) {
            Vueset(state.dashboard.data.data, index, task);
        }
    },
    UPDATE_TASK_FROM_DASHBOARD_V2(state, { task }) {
        const index = state.dashboard.data.data.findIndex(
            (item) => item.slug === task.slug
        );
        if (index > -1) {
            Vueset(state.dashboard.data.data, index, task);
        }
    },
    SET_SIDEBAR_MOBILE_TOGGLE(state, value) {
        state.sidebarMobileToggle = value;
    },
    SET_IS_MOBILE(state, value) {
        state.isMobile = value;
    },
    SET_IS_LOGGING_OUT(state, value) {
        state.loggingOut = value;
    },
    UNSHIFT_DASHBOARD(state, task) {
        state.dashboard.data.data.unshift(task);
    },
    UPDATE_CURRENT_TASK_STATUS(state, payload) {
        state.currentTaskStatus = payload;
    },
};

export const actions = {
    UPDATE_CONTROL_VALUE({ commit }, { controlName, value }) {
        commit("SET_CONTROL_VALUE", { controlName, value });
    },
    GET_ACCOUNTS() {
        const url = `/accounts`;
        return this.$whyApi.get({
            url,
            mutation: "webGeneral/SET_ACCOUNTS",
            errorMessage: "Error getting accounts",
            notify: this.$notify,
        });
    },
    GET_PROJECTS(state, { params }) {
        const url = `/user/projects`;
        return this.$whyApi.get({
            url,
            mutation: "webGeneral/SET_PROJECTS_FOR_ACCOUNT",
            errorMessage: "Error getting projects",
            notify: this.$notify,
            params,
        });
    },
    SET_DEFAULT_PROJECT(state, { accountSlug }) {
        const url = `/accounts/${accountSlug}/set-default`;
        return this.$whyApi.post({
            url,
            mutation: "webGeneral/SET_DEFAULT_PROJECT",
            errorMessage: "Error setting default project",
            notify: this.$notify,
        });
    },
    GET_PROJECTS_FOR_ACCOUNT(state, { accountSlug, params }) {
        const url = `/accounts/${accountSlug}/projects`;
        return this.$whyApi.get({
            url,
            mutation: "webGeneral/SET_PROJECTS_FOR_ACCOUNT",
            errorMessage: "Error getting projects",
            notify: this.$notify,
            params,
        });
    },
    RESTORE_PROJECT(state, { accountSlug, projectSlug }) {
        const url = `/accounts/${accountSlug}/${projectSlug}/restore`;
        return this.$whyApi.put({
            url,
            mutation: "webGeneral/SET_RESTORE_PROJECT",
            errorMessage: "Error updating project",
            notify: this.$notify,
        });
    },
    GET_DASHBOARD(context, { accountSlug, projectSlug, status, type, search }) {
        if (!accountSlug) {
            return;
        }
        let url = `/accounts/${accountSlug}/${projectSlug}/dashboard`;
        if (status) {
            context.commit("UPDATE_CURRENT_TASK_STATUS", status);
            url = `${url}?status=${status}`;
        }
        if (type) {
            url = `${url}${status ? "&" : "?"}type=${type}`;
        }
        if (search) {
            url = `${url}${status || type ? "&" : "?"}search=${search}`;
        }
        return this.$whyApi.get({
            url,
            mutation: "webGeneral/SET_DASHBOARD",
            errorMessage: "Error getting dashboard data",
            notify: this.$notify,
        });
    },
    GET_ACCOUNT(state, { accountSlug }) {
        const url = `/accounts/${accountSlug}`;
        return this.$whyApi.get({
            url,
            mutation: "webGeneral/SET_ACCOUNT",
            errorMessage: "Error getting account",
            notify: this.$notify,
        });
    },
    GET_PROJECT(state, { accountSlug, projectSlug }) {
        const url = `/accounts/${accountSlug}/${projectSlug}`;
        return this.$whyApi.get({
            url,
            mutation: "webGeneral/SET_PROJECT_STATE",
            errorMessage: "Error getting project",
            notify: this.$notify,
        });
    },
    CREATE_PROJECT(state, data) {
        const { accountSlug, projectSlug, ...payload } = data;
        const url = `/accounts/${accountSlug}/projects/create`;
        return this.$whyApi.post({
            url,
            data: payload,
            mutation: "webGeneral/SET_PROJECT_CREATION",
            errorMessage: "Error creating project",
            notify: this.$notify,
        });
    },
    UPDATE_PROJECT(state, data) {
        const { accountSlug, projectSlug, ...payload } = data;
        const url = `/accounts/${accountSlug}/${projectSlug}`;
        return this.$whyApi.put({
            url,
            data: payload,
            mutation: "webGeneral/SET_PROJECT_UPDATE_STATE",
            errorMessage: "Error updating project",
            notify: this.$notify,
        });
    },
    CREATE_ACCOUNT(state, { accountSlug, ...data }) {
        const url = `/accounts/create`;
        return this.$whyApi.post({
            url,
            data,
            mutation: "webGeneral/SET_CREATE_ACCOUNT_STATE",
            errorMessage: "Error creating account",
            notify: this.$notify,
            handleMedia: true,
        });
    },
    UPDATE_ACCOUNT(state, { accountSlug, ...data }) {
        const url = `/accounts/${accountSlug}`;
        return this.$whyApi.post({
            url,
            data,
            mutation: "webGeneral/SET_UPDATE_ACCOUNT_STATE",
            errorMessage: "Error updating account",
            notify: this.$notify,
            handleMedia: true,
        });
    },
    DELETE_ACCOUNT(state, { accountSlug }) {
        const url = `/accounts/${accountSlug}`;
        return this.$whyApi.delete({
            url,
            mutation: "webGeneral/SET_DELETE_ACCOUNT_STATE",
            errorMessage: "Error deleting account",
            notify: this.$notify,
        });
    },
    DELETE_PROJECT(state, data) {
        const { accountSlug, projectSlug } = data;
        const url = `/accounts/${accountSlug}/${projectSlug}`;
        return this.$whyApi.delete({
            url,
            mutation: "webGeneral/SET_PROJECT_DELETE_STATE",
            errorMessage: "Error deleting project",
            notify: this.$notify,
        });
    },
    GET_GLOBAL_SETUP_VALUES(context, payload = {}) {
        const force = payload?.force === true;

        if (!force && context.state.globalSetupValuesState.data) {
            return Promise.resolve({ data: context.state.globalSetupValuesState.data });
        }

        if (globalSetupValuesRequest) {
            return globalSetupValuesRequest;
        }

        globalSetupValuesRequest = this.$whyApi
            .get({
                url: `/global-setup-values`,
                mutation: "webGeneral/SET_GLOBAL_SETUP_VALUES",
                errorMessage: "Error getting config data",
                notify: this.$notify,
            })
            .finally(() => {
                globalSetupValuesRequest = null;
            });

        return globalSetupValuesRequest;
    },
};
